import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { useUserActions } from './hooks/useUserActions';
import { Box, CircularProgress } from '@mui/material';
import Layout from './components/Layout';
// Login eager: necessario sem espera de chunk (1a tela do app)
import Login from './pages/Login';
// Demais paginas lazy — code splitting reduz first-load de ~1.24MB pra ~400KB.
// Cada rota baixa seu chunk so quando navegada. Vendor/MUI fica compartilhado.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Licenses = lazy(() => import('./pages/Licenses'));
const Users = lazy(() => import('./pages/Users'));
const Roles = lazy(() => import('./pages/Roles'));
const Holidays = lazy(() => import('./pages/Holidays'));
const ContactLinks = lazy(() => import('./pages/ContactLinks'));
const Downloads = lazy(() => import('./pages/Downloads'));
const Settings = lazy(() => import('./pages/Settings'));
const Customers = lazy(() => import('./pages/Customers'));
const CustomerPortalUsers = lazy(() => import('./pages/CustomerPortalUsers'));
const ReplicadorClients = lazy(() => import('./pages/replicador/ReplicadorClients'));
const ReplicadorEndpoints = lazy(() => import('./pages/replicador/ReplicadorEndpoints'));
const ReplicadorSyncTables = lazy(() => import('./pages/replicador/ReplicadorSyncTables'));
const ReplicadorDashboard = lazy(() => import('./pages/replicador/ReplicadorDashboard'));
const ReplicadorBackups = lazy(() => import('./pages/replicador/ReplicadorBackups'));
const ReplicadorLogs = lazy(() => import('./pages/replicador/ReplicadorLogs'));
const ReplicadorClientConfig = lazy(() => import('./pages/replicador/ReplicadorClientConfig'));
const MonitorSistema = lazy(() => import('./pages/replicador/MonitorSistema'));
const SupportRequests = lazy(() => import('./pages/SupportRequests'));
const Atendimento = lazy(() => import('./pages/Atendimento'));
const AtendimentoChat = lazy(() => import('./pages/atendimento/AtendimentoChat'));
const MonitorCertificados = lazy(() => import('./pages/replicador/MonitorCertificados'));
const OperationalAlerts = lazy(() => import('./pages/replicador/OperationalAlerts'));
const TefUpdates = lazy(() => import('./pages/TefUpdates'));
const SyncConfig = lazy(() => import('./pages/SyncConfig'));
const PortalLogin = lazy(() => import('./pages/portal/PortalLogin'));
const PortalDashboard = lazy(() => import('./pages/portal/PortalDashboard'));
const PortalRegister = lazy(() => import('./pages/portal/PortalRegister'));
const HubAdmin = lazy(() => import('./pages/HubAdmin'));
const HubPlansAdmin = lazy(() => import('./pages/HubPlansAdmin'));
const HubMarketplace = lazy(() => import('./pages/HubMarketplace'));
const HubDashboard = lazy(() => import('./pages/HubDashboard'));
const HubSubscriptionsAdmin = lazy(() => import('./pages/HubSubscriptionsAdmin'));
const HubPaymentsAdmin = lazy(() => import('./pages/HubPaymentsAdmin'));
const HubIntegrationAdmin = lazy(() => import('./pages/HubIntegrationAdmin'));
const HubMarketplaceEmbed = lazy(() => import('./pages/HubMarketplaceEmbed'));
const HubMarketplaceReturn = lazy(() => import('./pages/HubMarketplaceReturn'));
const MarketplaceUsersAdmin = lazy(() => import('./pages/MarketplaceUsersAdmin'));

const PageSuspenseFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4, minHeight: 200 }}>
    <CircularProgress size={28} />
  </Box>
);

function PrivateRoute({ children, adminOnly = false, permission = null, requireAction = null }) {
  const { user, loading } = useAuth();
  const { hasAction, loading: actionsLoading } = useUserActions();

  if (loading) {
    return <div>Carregando...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Em admin.otimizapp.com.br, fallback é /hub/dashboard (não /atendimento que pertence ao portal Otimize)
  const defaultLanding = (typeof window !== 'undefined' && window.location.hostname.startsWith('admin.otimizapp.'))
    ? '/hub/dashboard' : '/atendimento';

  // adminOnly: apenas admin/superadmin (telas reais de gestão: Users, Settings)
  if (adminOnly && !['admin', 'superadmin'].includes(user.role)) {
    return <Navigate to={defaultLanding} />;
  }

  // permission: legacy (user_permission flags). superadmin sempre passa.
  if (permission && user.role !== 'superadmin' && !user.permissions?.[permission]) {
    return <Navigate to={defaultLanding} />;
  }

  // requireAction: novo RBAC granular. superadmin sempre passa (backend dá tudo).
  // Espera carregar actions pra evitar redirect prematuro.
  if (requireAction && user.role !== 'superadmin') {
    if (actionsLoading) return <div>Carregando permissões...</div>;
    if (!hasAction(requireAction)) return <Navigate to={defaultLanding} />;
  }

  return children;
}

function App() {
  const isMarketplaceHost = typeof window !== 'undefined' &&
    (window.location.hostname.startsWith('marketplace.') ||
     window.location.hostname.startsWith('hub.'));

  if (isMarketplaceHost) {
    return (
      <Suspense fallback={<PageSuspenseFallback />}>
        <Routes>
          {/* Embed iframe Multiflow PRO — entra antes do wildcard */}
          <Route path='/hub/marketplace' element={<HubMarketplaceEmbed />} />
          <Route path='/hub/marketplace/return' element={<HubMarketplaceReturn />} />
          {/* Marketplace standalone — default */}
          <Route path='*' element={<HubMarketplace />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<PageSuspenseFallback />}>
      <Routes>
      {/* Portal do Cliente (publico) */}
      <Route path="/portal" element={<PortalLogin />} />
      <Route path="/portal/register" element={<PortalRegister />} />
      <Route path="/portal/dashboard" element={<PortalDashboard />} />

      {/* Embed iframe Multiflow PRO */}
      <Route path="/hub/marketplace" element={<HubMarketplaceEmbed />} />
      <Route path="/hub/marketplace/return" element={<HubMarketplaceReturn />} />

      {/* Admin Login */}
      <Route path="/login" element={<Login />} />

      {/* Chat full-screen (top-nav proprio, sem sidebar) */}
      <Route path="/atendimento/chat" element={
        <PrivateRoute requireAction="atendimento.view">
          <AtendimentoChat />
        </PrivateRoute>
      } />

      {/* Admin Routes */}
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to={(typeof window !== 'undefined' && window.location.hostname.startsWith('admin.otimizapp.')) ? '/hub/dashboard' : '/atendimento'} replace />} />
        <Route path="dashboard" element={
          <PrivateRoute requireAction="dashboard.view">
            <Dashboard />
          </PrivateRoute>
        } />
        <Route path="licenses" element={
          <PrivateRoute requireAction="license.list">
            <Licenses />
          </PrivateRoute>
        } />
        <Route path="users" element={
          <PrivateRoute adminOnly requireAction="users.list">
            <Users />
          </PrivateRoute>
        } />
        <Route path="roles" element={
          <PrivateRoute adminOnly requireAction="users.permissions.update">
            <Roles />
          </PrivateRoute>
        } />
        <Route path="holidays" element={
          <PrivateRoute requireAction="settings.update">
            <Holidays />
          </PrivateRoute>
        } />
        <Route path="contact-links" element={
          <PrivateRoute requireAction="atendimento.contact_link.scan">
            <ContactLinks />
          </PrivateRoute>
        } />

        <Route path="support-requests" element={
          <PrivateRoute>
            <SupportRequests />
          </PrivateRoute>
        } />
        <Route path="downloads" element={
          <PrivateRoute requireAction="downloads.list">
            <Downloads />
          </PrivateRoute>
        } />
        <Route path="settings" element={
          <PrivateRoute adminOnly requireAction="settings.view">
            <Settings />
          </PrivateRoute>
        } />
        <Route path="customers" element={
          <PrivateRoute requireAction="customers.list">
            <Customers />
          </PrivateRoute>
        } />
        <Route path="atendimento" element={
          <PrivateRoute requireAction="atendimento.view">
            <Atendimento />
          </PrivateRoute>
        } />
        <Route path="portal-users" element={
          <PrivateRoute requireAction="portal_users.list">
            <CustomerPortalUsers />
          </PrivateRoute>
        } />
        <Route path="hub-admin" element={
          <PrivateRoute adminOnly>
            <HubAdmin />
          </PrivateRoute>
        } />
        <Route path="hub/plans-admin" element={
          <PrivateRoute adminOnly>
            <HubPlansAdmin />
          </PrivateRoute>
        } />
        <Route path="hub/dashboard" element={
          <PrivateRoute adminOnly>
            <HubDashboard />
          </PrivateRoute>
        } />
        <Route path="hub/subscriptions" element={
          <PrivateRoute adminOnly>
            <HubSubscriptionsAdmin />
          </PrivateRoute>
        } />
        <Route path="hub/payments" element={
          <PrivateRoute adminOnly>
            <HubPaymentsAdmin />
          </PrivateRoute>
        } />
        <Route path="hub/integration" element={
          <PrivateRoute adminOnly>
            <HubIntegrationAdmin />
          </PrivateRoute>
        } />
        <Route path="marketplace/users" element={
          <PrivateRoute adminOnly>
            <MarketplaceUsersAdmin />
          </PrivateRoute>
        } />
        <Route path="sync-config" element={
          <PrivateRoute adminOnly requireAction="customers.sync.config">
            <SyncConfig />
          </PrivateRoute>
        } />
        {/* Monitor Routes (ex-Replicador) */}
        <Route path="monitor/dashboard" element={
          <PrivateRoute requireAction="monitor.dashboard.view">
            <ReplicadorDashboard />
          </PrivateRoute>
        } />
        <Route path="monitor/clients" element={
          <PrivateRoute requireAction="monitor.clients.view">
            <ReplicadorClients />
          </PrivateRoute>
        } />
        <Route path="monitor/backups" element={
          <PrivateRoute requireAction="monitor.backups.view">
            <ReplicadorBackups />
          </PrivateRoute>
        } />
        <Route path="monitor/endpoints" element={
          <PrivateRoute requireAction="monitor.endpoints.view">
            <ReplicadorEndpoints />
          </PrivateRoute>
        } />
        <Route path="monitor/sync-tables" element={
          <PrivateRoute requireAction="monitor.tables.view">
            <ReplicadorSyncTables />
          </PrivateRoute>
        } />
        <Route path="monitor/logs" element={
          <PrivateRoute requireAction="monitor.logs.view">
            <ReplicadorLogs />
          </PrivateRoute>
        } />
        <Route path="monitor/system" element={
          <PrivateRoute requireAction="monitor.system.view">
            <MonitorSistema />
          </PrivateRoute>
        } />
        <Route path="monitor/certificates" element={
          <PrivateRoute requireAction="monitor.certificates.view">
            <MonitorCertificados />
          </PrivateRoute>
        } />
        <Route path="monitor/alerts" element={
          <PrivateRoute requireAction="monitor.alerts.view">
            <OperationalAlerts />
          </PrivateRoute>
        } />
        <Route path="tef-updates" element={
          <PrivateRoute requireAction="tef_updates.view">
            <TefUpdates />
          </PrivateRoute>
        } />
        <Route path="monitor/clients/:clientId/config" element={
          <PrivateRoute requireAction="monitor.client.update">
            <ReplicadorClientConfig />
          </PrivateRoute>
        } />
        {/* Redirect old replicador routes to monitor */}
        <Route path="replicador/*" element={<Navigate to="/monitor/dashboard" replace />} />
      </Route>
      </Routes>
    </Suspense>
  );
}

export default App;
